import { useEffect, useState } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';
import { datadogTrackEvent, logError } from 'utils/datadog';
import { env } from '../../constants';
import { clearDraftThenLogout } from './useLogout';

interface DomainClaims {
  email: string;
  identity: string[];
  uuid: string;
}

const API_AUDIENCE = env.VITE_API_BASE_URL;

/**
 * Auth0 errors that simply mean the session has ended — the refresh token expired,
 * was rotated/already-used, is missing, or interactive login is required. These are
 * an expected part of the auth lifecycle (common when a user returns to the app after
 * being away), NOT application errors. Recording them via `logError` made the forced
 * logout the app's #1 RUM error issue and masked real bugs, so we record them as a
 * plain action instead — re-authentication via logout is the correct response.
 */
const EXPECTED_SESSION_END_ERRORS = new Set([
  'invalid_grant', // "Unknown or invalid refresh token" — expired / rotated / reused
  'missing_refresh_token',
  'login_required',
  'consent_required'
]);

const isExpectedSessionEnd = (error: unknown): boolean => {
  const code = (error as { error?: string } | null)?.error;
  return code !== undefined && EXPECTED_SESSION_END_ERRORS.has(code);
};

/**
 * Browser fetch-failure messages — Safari "Load failed", Chrome "Failed to fetch",
 * Firefox "NetworkError". A flaky/transient connection during the silent token
 * refresh surfaces as one of these (or Auth0's own 'timeout' error). These are NOT
 * a reason to log the user out: the session is still valid, the network just
 * hiccupped — so we retry, and never record them as errors.
 */
const NETWORK_ERROR_PATTERN = /load failed|failed to fetch|networkerror/i;

const MAX_TOKEN_REFRESH_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;

const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

const isTransientNetworkError = (error: unknown): boolean => {
  if ((error as { error?: string } | null)?.error === 'timeout') {
    return true;
  }
  const message = (error as { message?: string } | null)?.message ?? '';
  return NETWORK_ERROR_PATTERN.test(message);
};

export const useAccessToken = () => {
  const [token, setToken] = useState<string | undefined>(undefined);

  const { logout, isLoading, isAuthenticated, getAccessTokenSilently } =
    useAuth0();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const getToken = async () => {
        for (let attempt = 0; ; attempt += 1) {
          try {
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: API_AUDIENCE
              }
            });

            setToken(accessToken);
            return;
          } catch (error) {
            if (isExpectedSessionEnd(error)) {
              // Expected session expiry — track as an action, not an error, then re-auth.
              datadogTrackEvent('auth_session_ended', {
                reason: (error as { error?: string }).error
              });
              // Forced logout: clear the local CV draft PII first (best-effort).
              await clearDraftThenLogout(logout);
              return;
            }

            if (isTransientNetworkError(error)) {
              if (attempt < MAX_TOKEN_REFRESH_RETRIES) {
                // A flaky connection must not log the user out — back off and retry.
                await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
                continue;
              }
              // Still failing after retries: leave the (still-valid) session intact.
              // These consumers re-mount on navigation, so a later mount re-runs this
              // effect and retries. Record as an action, never as an error.
              datadogTrackEvent('auth_token_refresh_failed', {
                reason: 'network'
              });
              return;
            }

            // Genuinely unexpected — surface it and force a clean re-auth.
            logError('Could not get access token', error);
            // Forced logout: clear the local CV draft PII first (best-effort).
            await clearDraftThenLogout(logout);
            return;
          }
        }
      };

      void getToken();
    }
  }, [isLoading, isAuthenticated, logout, setToken, getAccessTokenSilently]);

  return { token, isLoading };
};

interface MyUser extends Omit<User, '[key: string]'> {
  [key: string]: DomainClaims | undefined;
}

export const useUserUUID = () => {
  const claims = 'https://zenjob.com';
  const { user } = useAuth0();

  const uuid = (user as MyUser)?.[claims]?.uuid;

  return uuid;
};
